import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Shared formatting, styles and small UI pieces for the Stores screens
// (StoresModule.js and StoresPurchase.js).

export const fmtQty = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
export const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (d) => (d ? new Date(String(d).length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const S = {
  page: { padding: '1.25rem 1.5rem', background: '#f8fafc', minHeight: '80vh' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' },
  h2: { margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#1e3a5f' },
  sub: { margin: '0.2rem 0 0', color: '#64748b', fontSize: '0.85rem' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1rem', marginBottom: '1rem' },
  bar: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' },
  lbl: { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' },
  inp: { padding: '0.42rem 0.55rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.86rem', background: '#fff', boxSizing: 'border-box' },
  tableWrap: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' },
  th: { textAlign: 'left', padding: '0.5rem 0.6rem', background: '#f1f5f9', color: '#475569', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.45rem 0.6rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  err: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' },
  ok: { background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' },
  empty: { padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.88rem' },
  tab: (on) => ({ padding: '0.45rem 0.9rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', color: on ? '#1e40af' : '#64748b', borderBottom: on ? '2px solid #1e40af' : '2px solid transparent' }),
  pill: (bg, fg) => ({ display: 'inline-block', background: bg, color: fg, padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }),
  modalBack: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 1rem', overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, padding: '1.25rem', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
};

export function Field({ label, children, style }) {
  return (
    <label style={{ display: 'block', ...style }}>
      <span style={S.lbl}>{label}</span>
      {children}
    </label>
  );
}

export const itemOptions = (items) => items.map((i) => ({ value: i.id, label: `${i.item_name} (${i.item_code})` }));

// Drop-in for a long <select>: a button that opens a list with a search box.
// options: [{ value, label }]; onChange receives the chosen value (string).
// The list is portalled and position: fixed so table scroll wrappers and modals
// don't clip it, and so clicks in it don't re-trigger an enclosing <label>.
export function SearchSelect({ value, onChange, options, placeholder = 'Choose…', style, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? options.filter((o) => words.every((w) => String(o.label).toLowerCase().includes(w))) : options;
  }, [options, q]);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const up = below < 280 && r.top > below;
    setPos({ left: r.left, width: Math.max(r.width, 220), ...(up ? { bottom: window.innerHeight - r.top + 2 } : { top: r.bottom + 2 }), maxH: Math.min(320, (up ? r.top : below) - 12) });
  }, []);
  useLayoutEffect(() => { if (open) place(); }, [open, place]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!popRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open, place]);
  useEffect(() => { listRef.current?.children[hi]?.scrollIntoView({ block: 'nearest' }); }, [hi]);

  const show = () => { if (disabled) return; setQ(''); setHi(Math.max(0, options.indexOf(selected))); setOpen(true); };
  const pick = (o) => { setOpen(false); if (o && String(o.value) !== String(value)) onChange(String(o.value)); btnRef.current?.focus(); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(shown[hi]); }
    else if (e.key === 'Escape' || e.key === 'Tab') setOpen(false);
  };

  return (
    <>
      <button type="button" ref={btnRef} disabled={disabled} onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={(e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); show(); } }}
        style={{ ...S.inp, ...style, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', color: selected && selected.value !== '' ? '#0f172a' : '#64748b', opacity: disabled ? 0.6 : 1 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <span style={{ fontSize: '0.65rem', color: '#64748b' }}>▼</span>
      </button>
      {open && pos && createPortal(
        <div ref={popRef} style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, zIndex: 2000, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 10px 30px rgba(15,23,42,0.2)', display: 'flex', flexDirection: 'column', maxHeight: pos.maxH }}>
          <input autoFocus value={q} placeholder="Search…" onKeyDown={onKey} onChange={(e) => { setQ(e.target.value); setHi(0); }}
            style={{ ...S.inp, margin: 6, width: 'calc(100% - 12px)' }} />
          <div ref={listRef} style={{ overflowY: 'auto', flex: 1 }}>
            {shown.map((o, i) => (
              <div key={o.value} onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
                style={{ padding: '0.38rem 0.65rem', fontSize: '0.85rem', cursor: 'pointer', background: i === hi ? '#dbeafe' : String(o.value) === String(value) ? '#f1f5f9' : '#fff', fontWeight: String(o.value) === String(value) ? 600 : 400 }}>
                {o.label}
              </div>
            ))}
          </div>
          {!shown.length && <div style={{ padding: '0.6rem', fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center' }}>No match</div>}
        </div>,
        document.body,
      )}
    </>
  );
}

const optText = (c) => React.Children.toArray(c).map((x) => (typeof x === 'object' ? optText(x.props?.children) : String(x))).join('');
const collectOptions = (children, out = []) => {
  React.Children.forEach(children, (c) => {
    if (!c || typeof c !== 'object') return;
    if (c.type === 'option') {
      const label = optText(c.props.children);
      out.push({ value: c.props.value ?? label, label });
    } else if (c.props?.children) collectOptions(c.props.children, out);
  });
  return out;
};

// <Select> takes the same props and <option> children as a native <select>
// (onChange gets { target: { value } }), rendered as a SearchSelect.
export function Select({ value, onChange, style, disabled, children }) {
  const options = collectOptions(children);
  const match = options.some((o) => String(o.value) === String(value ?? ''));
  return (
    <SearchSelect value={match ? value ?? '' : options[0]?.value} options={options} style={style} disabled={disabled} placeholder=""
      onChange={(v) => onChange && onChange({ target: { value: v } })} />
  );
}

export function Modal({ title, onClose, children, width }) {
  return (
    <div style={S.modalBack} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...S.modal, maxWidth: width || 900 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a5f' }}>{title}</h3>
          <button type="button" onClick={onClose} className="btn-muted btn-sm" aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
